/**
 * Comments on an announcement. The audience check is the announcement feed's own: a viewer may
 * comment on, or read the comments of, exactly the live announcements their feed shows them.
 * Everything else answers 404, so an announcement outside someone's audience is not revealed.
 *
 * Comments are plain text (no markup, no mentions) and go through the same DLP scan as posts.
 * Authors delete their own comments; the delete is soft so an admin can still see a removed comment
 * was there.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { SOCIAL_COMMENT_MAX_LENGTH } from '../social.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const announcementCommentSchema = z.object({
  id: uuidSchema,
  announcementId: uuidSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  isMine: z.boolean(),
  createdAt: instantSchema,
});
export type AnnouncementComment = z.infer<typeof announcementCommentSchema>;

export const announcementCommentBodySchema = z.object({
  body: z.string().trim().min(1).max(SOCIAL_COMMENT_MAX_LENGTH),
});

const announcementParams = z.object({ announcementId: uuidSchema });
const commentParams = z.object({ announcementId: uuidSchema, commentId: uuidSchema });

const read = routeMeta('announcement:read');

export const announcementCommentsContract = c.router(
  {
    listAnnouncementComments: {
      method: 'GET',
      path: '/:announcementId/comments',
      pathParams: announcementParams,
      responses: {
        200: z.object({ items: z.array(announcementCommentSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Comments on a live announcement the viewer can read, oldest first',
      metadata: read,
    },
    addAnnouncementComment: {
      method: 'POST',
      path: '/:announcementId/comments',
      pathParams: announcementParams,
      body: announcementCommentBodySchema,
      responses: {
        201: announcementCommentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Comment on a live announcement the viewer can read',
      metadata: read,
    },
    deleteAnnouncementComment: {
      method: 'DELETE',
      path: '/:announcementId/comments/:commentId',
      pathParams: commentParams,
      body: c.noBody(),
      responses: {
        200: z.object({ deleted: z.literal(true) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Delete one's own announcement comment (soft)",
      metadata: read,
    },
  },
  { pathPrefix: '/announcement-comments' },
);
